import { appendClientDebugAction } from "@/lib/debug/client-debug";
import {
  createRouteOwnerMachineState,
  reduceRouteOwnerMachine,
  type RouteOwnerMachineState,
  type RouteOwnerShadowDecision,
} from "./client-route-owner-machine";
import type { BottomNavHistoryCommitDecision } from "./client-navigation-store";
import type { ClientNavTransaction } from "./client-navigation-types";

const ROUTE_OWNER_MACHINE_SOURCE = "route-owner-machine";
const ROUTE_OWNER_MACHINE_DECISION_EVENT = "route-owner-shadow-decision";
const ROUTE_OWNER_MACHINE_MISMATCH_EVENT = "route-owner-shadow-mismatch";

let shadowState: RouteOwnerMachineState = createRouteOwnerMachineState();

function summarizeState(state: RouteOwnerMachineState): Record<string, unknown> {
  const latestOwner = state.owners.at(-1) ?? null;
  return {
    ownerCount: state.owners.length,
    currentOwnerId: state.currentOwnerId,
    currentHref: state.currentHref,
    latestOwner: latestOwner
      ? {
          transactionId: latestOwner.transactionId,
          source: latestOwner.source,
          reason: latestOwner.reason,
          to: latestOwner.to,
          toPath: latestOwner.toPath,
          consumedAtIso: latestOwner.consumedAtIso ?? null,
          expiredAtIso: latestOwner.expiredAtIso ?? null,
        }
      : null,
  };
}

function appendShadowDecision(details: Record<string, unknown>): void {
  appendClientDebugAction({
    source: ROUTE_OWNER_MACHINE_SOURCE,
    event: ROUTE_OWNER_MACHINE_DECISION_EVENT,
    details,
  });
}

function shadowSuppresses(decision: RouteOwnerShadowDecision | null): boolean {
  return decision?.kind === "suppress-commit" || decision?.kind === "reassert-owner";
}

function shadowReasserts(decision: RouteOwnerShadowDecision | null): boolean {
  return decision?.kind === "reassert-owner";
}

function shadowDropsRestore(decision: RouteOwnerShadowDecision | null): boolean {
  return decision?.kind === "drop-restore";
}

export type RouteOwnerHistoryCommitResult = {
  decision: RouteOwnerShadowDecision | null;
  authoritySuppressed: boolean;
};

export type RouteOwnerSameTargetRenderedRouteResult = {
  decision: RouteOwnerShadowDecision | null;
  authorityReasserted: boolean;
};

export function isRouteOwnerHistoryCommitAuthoritySuppression(
  decision: RouteOwnerShadowDecision | null
): boolean {
  return (
    decision?.kind === "suppress-commit" &&
    (
      decision.reason === "newer-owner-protects-current-route" ||
      decision.reason === "inactive-owner-commit"
    )
  );
}

export function isRouteOwnerSameTargetRenderedRouteAuthorityReassertion(
  decision: RouteOwnerShadowDecision | null
): boolean {
  return (
    decision?.kind === "reassert-owner" &&
    decision.reason === "same-target-rendered-route-split-brain"
  );
}

export function recordRouteOwnerShadowTransaction(transaction: ClientNavTransaction, nowMs = Date.now()): void {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "transaction-recorded",
    atMs: nowMs,
    transaction,
  });
  shadowState = reduced.state;
}

export function recordRouteOwnerShadowConsumed(transaction: ClientNavTransaction, reason: string, nowMs = Date.now()): void {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "owner-consumed",
    atMs: nowMs,
    transactionId: transaction.transactionId,
    reason,
  });
  shadowState = reduced.state;
}

export function recordRouteOwnerShadowExpired(transaction: ClientNavTransaction, reason: string, nowMs = Date.now()): void {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "owner-expired",
    atMs: nowMs,
    transactionId: transaction.transactionId,
    reason,
  });
  shadowState = reduced.state;
}

export function recordRouteOwnerShadowHistoryCommit(params: {
  transaction?: ClientNavTransaction | null;
  decision?: BottomNavHistoryCommitDecision | null;
  productionIgnored?: boolean;
  productionReason?: string;
  mode?: "shadow" | "authority";
  historyEvent: "pushState" | "replaceState";
  targetHref: string;
  currentHrefBeforeCommit: string;
  nowMs?: number;
}): RouteOwnerHistoryCommitResult {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "history-commit-observed",
    atMs: params.nowMs ?? Date.now(),
    historyEvent: params.historyEvent,
    transactionId: params.transaction?.transactionId ?? null,
    targetHref: params.targetHref,
    currentHrefBeforeCommit: params.currentHrefBeforeCommit,
  });
  shadowState = reduced.state;

  const shadowDecision = reduced.decision;
  const authoritySuppressed = isRouteOwnerHistoryCommitAuthoritySuppression(shadowDecision);
  const productionIgnored =
    params.productionIgnored ?? ((params.decision?.ignore ?? false) || authoritySuppressed);
  const shadowIgnored = shadowSuppresses(shadowDecision);
  const productionReason =
    params.productionReason ??
    (
      authoritySuppressed
        ? `route-owner-machine:${shadowDecision?.reason ?? "suppress-commit"}`
        : params.decision?.reason ?? "not-mobile-route-transaction"
    );
  const baseDetails = {
    mode: params.mode ?? "shadow",
    transactionId: params.transaction?.transactionId ?? null,
    historyEvent: params.historyEvent,
    productionIgnored,
    productionReason,
    routeOwnerAuthority: authoritySuppressed ? ROUTE_OWNER_MACHINE_SOURCE : null,
    shadowIgnored,
    shadowDecision,
    state: summarizeState(shadowState),
  };

  appendShadowDecision(baseDetails);

  if (shadowDecision && productionIgnored !== shadowIgnored) {
    appendClientDebugAction({
      source: ROUTE_OWNER_MACHINE_SOURCE,
      event: ROUTE_OWNER_MACHINE_MISMATCH_EVENT,
      details: {
        ...baseDetails,
        mismatchKind: "ignore-decision",
      },
    });
  }

  return { decision: shadowDecision, authoritySuppressed };
}

export function recordRouteOwnerShadowPopstate(params: {
  targetHref: string;
  currentHrefBeforeCommit: string;
  productionReason?: string;
  nowMs?: number;
}): void {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "history-commit-observed",
    atMs: params.nowMs ?? Date.now(),
    historyEvent: "popstate",
    transactionId: null,
    targetHref: params.targetHref,
    currentHrefBeforeCommit: params.currentHrefBeforeCommit,
  });
  shadowState = reduced.state;

  const shadowDecision = reduced.decision;
  const productionIgnored = false;
  const shadowIgnored = shadowSuppresses(shadowDecision);
  const baseDetails = {
    mode: "shadow",
    historyEvent: "popstate",
    productionIgnored,
    productionReason: params.productionReason ?? "popstate",
    shadowIgnored,
    shadowDecision,
    state: summarizeState(shadowState),
  };

  appendShadowDecision(baseDetails);

  if (shadowDecision && productionIgnored !== shadowIgnored) {
    appendClientDebugAction({
      source: ROUTE_OWNER_MACHINE_SOURCE,
      event: ROUTE_OWNER_MACHINE_MISMATCH_EVENT,
      details: {
        ...baseDetails,
        mismatchKind: "popstate-decision",
      },
    });
  }
}

export function recordRouteOwnerShadowNavigationRequest(params: {
  transaction: ClientNavTransaction;
  targetHref: string;
  currentHref: string;
  renderedCurrentPath?: string | null;
  productionDropped: boolean;
  productionReason: string;
  nowMs?: number;
}): void {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "navigation-request-observed",
    atMs: params.nowMs ?? Date.now(),
    transactionId: params.transaction.transactionId,
    targetHref: params.targetHref,
    currentHref: params.currentHref,
    renderedCurrentPath: params.renderedCurrentPath ?? null,
  });
  shadowState = reduced.state;

  const shadowDecision = reduced.decision;
  const shadowDropped = shadowSuppresses(shadowDecision);
  const baseDetails = {
    mode: "shadow",
    transactionId: params.transaction.transactionId,
    targetHref: params.targetHref,
    currentHref: params.currentHref,
    renderedCurrentPath: params.renderedCurrentPath ?? null,
    productionDropped: params.productionDropped,
    productionReason: params.productionReason,
    shadowDropped,
    shadowDecision,
    state: summarizeState(shadowState),
  };

  appendShadowDecision(baseDetails);

  if (shadowDecision && params.productionDropped !== shadowDropped) {
    appendClientDebugAction({
      source: ROUTE_OWNER_MACHINE_SOURCE,
      event: ROUTE_OWNER_MACHINE_MISMATCH_EVENT,
      details: {
        ...baseDetails,
        mismatchKind: "navigation-request-decision",
      },
    });
  }
}

export type RouteOwnerShadowSavedRouteRestoreResult = {
  dropped: boolean;
  decision: RouteOwnerShadowDecision | null;
};

export function recordRouteOwnerShadowSavedRouteRestore(params: {
  targetHref: string;
  currentHrefBeforeCommit: string;
  navigationType?: string | null;
  productionDropped?: boolean;
  productionReason?: string;
  nowMs?: number;
}): RouteOwnerShadowSavedRouteRestoreResult {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "saved-route-restore-observed",
    atMs: params.nowMs ?? Date.now(),
    targetHref: params.targetHref,
    currentHrefBeforeCommit: params.currentHrefBeforeCommit,
    navigationType: params.navigationType ?? null,
  });
  shadowState = reduced.state;

  const shadowDecision = reduced.decision;
  const shadowDropped = shadowDropsRestore(shadowDecision);
  const productionDropped = params.productionDropped ?? shadowDropped;
  const productionReason = params.productionReason ?? (
    shadowDropped
      ? shadowDecision?.reason === "client-home-entry-owner"
        ? "client-home-entry-owner"
        : "fresh-explicit-navigation"
      : "route-owner-allow-restore"
  );
  const baseDetails = {
    mode: "shadow",
    productionDropped,
    productionReason,
    shadowDropped,
    shadowDecision,
    navigationType: params.navigationType ?? null,
    state: summarizeState(shadowState),
  };

  appendShadowDecision(baseDetails);

  if (shadowDecision && productionDropped !== shadowDropped) {
    appendClientDebugAction({
      source: ROUTE_OWNER_MACHINE_SOURCE,
      event: ROUTE_OWNER_MACHINE_MISMATCH_EVENT,
      details: {
        ...baseDetails,
        mismatchKind: "restore-decision",
      },
    });
  }

  return { dropped: shadowDropped, decision: shadowDecision };
}

export function recordRouteOwnerShadowRouteObserved(params: {
  href: string;
  pathname: string;
  nowMs?: number;
}): void {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "route-observed",
    atMs: params.nowMs ?? Date.now(),
    href: params.href,
  });
  shadowState = reduced.state;

  appendShadowDecision({
    mode: "shadow",
    productionObserved: true,
    productionReason: "route-observed",
    pathname: params.pathname,
    shadowDecision: reduced.decision,
    state: summarizeState(shadowState),
  });
}

export function recordRouteOwnerShadowSameTargetRenderedRoute(params: {
  transaction: ClientNavTransaction;
  targetHref: string;
  currentHref: string;
  renderedCurrentPath: string;
  mode?: "shadow" | "authority";
  productionReasserted?: boolean;
  productionReason?: string;
  nowMs?: number;
}): RouteOwnerSameTargetRenderedRouteResult {
  const reduced = reduceRouteOwnerMachine(shadowState, {
    type: "same-target-rendered-route-observed",
    atMs: params.nowMs ?? Date.now(),
    transactionId: params.transaction.transactionId,
    targetHref: params.targetHref,
    currentHref: params.currentHref,
    renderedCurrentPath: params.renderedCurrentPath,
  });
  shadowState = reduced.state;

  const shadowDecision = reduced.decision;
  const authorityReasserted = isRouteOwnerSameTargetRenderedRouteAuthorityReassertion(shadowDecision);
  const productionReasserted = params.productionReasserted ?? (
    params.mode === "authority" ? authorityReasserted : true
  );
  const shadowReasserted = shadowReasserts(shadowDecision);
  const productionReason =
    params.productionReason ??
    (
      params.mode === "authority" && authorityReasserted
        ? `route-owner-machine:${shadowDecision?.reason ?? "reassert-owner"}`
        : "same-target-rendered-route"
    );
  const baseDetails = {
    mode: params.mode ?? "shadow",
    transactionId: params.transaction.transactionId,
    targetHref: params.targetHref,
    currentHref: params.currentHref,
    renderedCurrentPath: params.renderedCurrentPath,
    productionReasserted,
    productionReason,
    routeOwnerAuthority:
      params.mode === "authority" && authorityReasserted ? ROUTE_OWNER_MACHINE_SOURCE : null,
    routeOwnerDecisionKind: shadowDecision?.kind ?? null,
    routeOwnerDecisionReason: shadowDecision?.reason ?? null,
    routeOwnerId: shadowDecision?.ownerId ?? null,
    routeOwnerSource: shadowDecision?.ownerSource ?? null,
    routeOwnerHref: shadowDecision?.ownerHref ?? null,
    shadowReasserted,
    shadowDecision,
    state: summarizeState(shadowState),
  };

  appendShadowDecision(baseDetails);

  if (!shadowReasserted) {
    appendClientDebugAction({
      source: ROUTE_OWNER_MACHINE_SOURCE,
      event: ROUTE_OWNER_MACHINE_MISMATCH_EVENT,
      details: {
        ...baseDetails,
        mismatchKind: "reassert-decision",
      },
    });
  }

  return { decision: shadowDecision, authorityReasserted };
}

export function readRouteOwnerShadowState(): RouteOwnerMachineState {
  return shadowState;
}

export function clearRouteOwnerShadowState(): void {
  shadowState = createRouteOwnerMachineState(
    typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/client"
  );
}
